import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { EMPTY, fromEvent, interval, merge, Observable } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { AppConfig } from './appconfig';
import { AuthService } from './route/auth/auth.service';
import { SplitViewObserverService } from './shared/components/split-view/split-view-observer.service';
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from './shared/dialogs/error-dialog/error-dialog.component';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

@Component({
  selector: 'lfw-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [RouterOutlet]
})
export class AppComponent implements OnInit {
  @HostListener('window:resize', ['$event'])
  notifyMoved(event: any): void {
    this.splitViewObserver.notifyMoved({ gutterNumber: 0 });
  }

  @HostListener('beforeinstallprompt', ['$event'])
  promptPWAInstall(event: BeforeInstallPromptEvent): void {
    event.preventDefault();
    if (this.appConfig.isMobileApp()) {
      console.info('remove manifest (either mobile app or enablepwa is false ');
      document.querySelector('link[rel="manifest"]')?.remove();
      return;
    }
    event.userChoice.then((choice) => {
      if (choice.outcome === 'dismissed') {
        console.info('user denied installing pwa');
      }
    });
    event.prompt();
  }

  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly splitViewObserver = inject(SplitViewObserverService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly dialog = inject(MatDialog);
  private readonly appConfig = inject(AppConfig);

  private runningVersionHash?: string;
  private availableVersionHash?: string;
  private updatePromptOpen = false;

  ngOnInit(): void {
    // Not chained to the login sequence: update checks need no token, and that sequence returns
    // early when offline and redirects when already logged in, which would race the subscriptions.
    this.initializeServiceWorker();

    this.authService.runInitialLoginSequence();

    if (this.appConfig.isMobileApp()) {
      console.info('removing manifest ... ');
      document.querySelector('link[rel="manifest"]')?.remove();
    }
  }

  initializeServiceWorker(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.unrecoverable
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap(() =>
            this.dialog
              .open(ErrorDialogComponent, {
                data: {
                  title: 'DIALOGS.SW_UPDATE_FAILED_TITLE',
                  message: 'DIALOGS.SW_UPDATE_FAILED_MESSAGE'
                }
              })
              .afterClosed()
          )
        )
        .subscribe(async () => {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.unregister();
          }
          location.reload();
        });

      this.swUpdate.versionUpdates
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((evt: VersionEvent) => this.rememberRunningVersion(evt)),
          filter((evt: VersionEvent): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          tap((evt: VersionReadyEvent) => {
            console.info(
              `currentVersion=[${JSON.stringify(evt.currentVersion)} | latestVersion=[${JSON.stringify(
                evt.latestVersion
              )}]`
            );
          }),
          switchMap(() => this.promptAndReload())
        )
        .subscribe();

      this.watchForUpdates();
    }
  }

  // Angular only checks for a new version at registration, so a long-running tab or a resumed PWA
  // would never learn that one exists.
  private watchForUpdates(): void {
    const onFocus = merge(
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
    ).pipe(debounceTime(1000));

    merge(interval(UPDATE_CHECK_INTERVAL_MS), onFocus)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.swUpdate.checkForUpdate().catch(() => false)),
        // A version downloaded in an earlier session emits NO_NEW_VERSION_DETECTED rather than
        // VERSION_READY, so this tab would otherwise keep running the old code unprompted.
        filter(() => this.isRunningOutdatedVersion),
        switchMap(() => this.promptAndReload())
      )
      .subscribe();
  }

  private rememberRunningVersion(evt: VersionEvent): void {
    if (evt.type === 'VERSION_READY') {
      this.runningVersionHash = evt.currentVersion.hash;
      this.availableVersionHash = evt.latestVersion.hash;
    } else if (evt.type === 'NO_NEW_VERSION_DETECTED') {
      this.availableVersionHash = evt.version.hash;
    }
  }

  private get isRunningOutdatedVersion(): boolean {
    return (
      this.runningVersionHash !== undefined &&
      this.availableVersionHash !== undefined &&
      this.runningVersionHash !== this.availableVersionHash
    );
  }

  private promptAndReload(): Observable<boolean> {
    if (this.updatePromptOpen) {
      return EMPTY;
    }
    this.updatePromptOpen = true;

    return this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: true,
        closeOnNavigation: false,
        data: {
          title: 'DIALOGS.SW_NEW_VERSION_TITLE',
          message: 'DIALOGS.SW_NEW_VERSION_MESSAGE',
          okIsDefault: true
        }
      })
      .afterClosed()
      .pipe(
        tap(() => (this.updatePromptOpen = false)),
        filter((confirmed) => !!confirmed),
        // Activating before reloading prevents the still-active worker from serving the old
        // version again, which also avoids a shell/lazy-chunk version mismatch.
        switchMap(() => this.swUpdate.activateUpdate()),
        tap(() => window.location.reload())
      );
  }
}
